import React, { useState, useEffect, useRef } from 'react';
import { sendChatCommand, clearConversationMemory, getNewSession } from '../services/apiClient';
import Card, { CardContent } from './ui/Card';
import Button from './ui/Button';
import { theme } from '../styles/design-system';
import {
  MessageSquare,
  Send,
  Trash2,
  Bot,
  User as UserIcon,
  Sparkles,
  TrendingUp,
  Bell,
  BarChart3,
  Brain
} from 'lucide-react';

const ChatWindow = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState('default');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        const response = await getNewSession();
        if (response.data.session_id) {
          setSessionId(response.data.session_id);
        }
      } catch (error) {
        console.error('Error initializing session:', error);
      }
    };
    initializeSession();
  }, []);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { user: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setIsLoading(true);

    try {
      const response = await sendChatCommand(userInput, sessionId);
      const botMessage = { bot: response.data.response, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { bot: 'Sorry, there was an error processing your request.', timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearConversation = async () => {
    if (!window.confirm('Are you sure you want to clear this conversation?')) return;

    try {
      await clearConversationMemory(sessionId);
      setMessages([]);
    } catch (error) {
      console.error('Error clearing conversation:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleExampleClick = async (queryText) => {
    // Auto-send the example query
    const userMessage = { user: queryText, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await sendChatCommand(queryText, sessionId);
      const botMessage = { bot: response.data.response, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { bot: 'Sorry, there was an error processing your request.', timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };


  const renderMessageContent = (content) => {
    if (typeof content === 'string') {
      return <div style={{ whiteSpace: 'pre-wrap' }}>{content}</div>;
    }

    if (typeof content === 'object' && content !== null) {
      // Check if it's a stock quote
      if (content.symbol && (content.last_price || content.price)) {
        const price = content.last_price || content.price;
        const change = content.change || 0;
        const changePercent = content.change_percent || 0;
        const isPositive = change >= 0;

        return (
          <div style={styles.stockCard}>
            <div style={styles.stockHeader}>
              <span style={styles.stockSymbol}>{content.symbol}</span>
              <span style={{
                ...styles.stockPrice,
                color: isPositive ? '#10b981' : '#ef4444'
              }}>
                ₹{price.toLocaleString()}
              </span>
            </div>
            <div style={styles.stockDetails}>
              <div style={styles.stockChange}>
                {isPositive ? <TrendingUp size={16} /> : <TrendingUp size={16} style={{ transform: 'scaleY(-1)' }} />}
                <span style={{ color: isPositive ? '#10b981' : '#ef4444' }}>
                  {change.toFixed(2)} ({changePercent.toFixed(2)}%)
                </span>
              </div>
              <div style={styles.stockGrid}>
                <div style={styles.stockItem}>
                  <span style={styles.stockLabel}>Open</span>
                  <span style={styles.stockValue}>{content.open?.toLocaleString()}</span>
                </div>
                <div style={styles.stockItem}>
                  <span style={styles.stockLabel}>High</span>
                  <span style={styles.stockValue}>{content.high?.toLocaleString()}</span>
                </div>
                <div style={styles.stockItem}>
                  <span style={styles.stockLabel}>Low</span>
                  <span style={styles.stockValue}>{content.low?.toLocaleString()}</span>
                </div>
                <div style={styles.stockItem}>
                  <span style={styles.stockLabel}>Close</span>
                  <span style={styles.stockValue}>{content.close?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Check if it's a portfolio response
      if (content.holdings && Array.isArray(content.holdings)) {
        return (
          <div style={styles.jsonContainer}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: 'var(--text-primary)' }}>
              Portfolio Value: ₹{content.total_portfolio_value?.toLocaleString()}
            </div>
            {content.holdings.map((item, idx) => (
              <div key={idx} style={{ ...styles.stockCard, minWidth: '100%', marginBottom: '8px' }}>
                <div style={styles.stockHeader}>
                  <span style={styles.stockSymbol}>{item.symbol}</span>
                  <span style={styles.stockPrice}>₹{item.current_price?.toLocaleString()}</span>
                </div>
                <div style={styles.stockDetails}>
                  <div style={styles.stockGrid}>
                    <div style={styles.stockItem}>
                      <span style={styles.stockLabel}>Qty</span>
                      <span style={styles.stockValue}>{item.quantity}</span>
                    </div>
                    <div style={styles.stockItem}>
                      <span style={styles.stockLabel}>Avg</span>
                      <span style={styles.stockValue}>{item.average_price}</span>
                    </div>
                    <div style={styles.stockItem}>
                      <span style={styles.stockLabel}>P&L</span>
                      <span style={{ ...styles.stockValue, color: item.pnl >= 0 ? '#10b981' : '#ef4444' }}>
                        {item.pnl?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      }

      // Check if it's a portfolio list (array)
      if (Array.isArray(content)) {
        return (
          <div style={styles.jsonContainer}>
            {content.map((item, idx) => (
              <div key={idx} style={{ marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                {renderMessageContent(item)}
              </div>
            ))}
          </div>
        );
      }

      // Fallback for other objects: Pretty print JSON
      return (
        <pre style={styles.jsonBlock}>
          {JSON.stringify(content, null, 2)}
        </pre>
      );
    }

    return String(content);
  };

  const exampleQueries = [
    { text: "What's the current price of INFY?", icon: <TrendingUp size={16} /> },
    { text: "Show me my portfolio holdings", icon: <BarChart3 size={16} /> },
    { text: "Analyze TCS stock performance", icon: <Brain size={16} /> },
    { text: "Create an alert for RELIANCE", icon: <Bell size={16} /> }
  ];

  const features = [
    { icon: <TrendingUp size={20} />, text: "Real-time stock prices and market data" },
    { icon: <BarChart3 size={20} />, text: "Portfolio analysis and tracking" },
    { icon: <Bell size={20} />, text: "Custom price alerts" },
    { icon: <Brain size={20} />, text: "AI-powered trading insights" },
    { icon: <Sparkles size={20} />, text: "Market trends and analytics" }
  ];

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroContent}>
          <div style={styles.heroTitle}>
            <MessageSquare size={32} />
            <span>AI Trading Assistant</span>
          </div>
          <p style={styles.heroSubtitle}>
            Your intelligent companion for real-time market insights and trading decisions
          </p>
        </div>
        <div style={styles.heroActions}>
          <Button
            onClick={handleClearConversation}
            variant="outline"
            style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
            icon={<Trash2 size={16} />}
            disabled={messages.length === 0}
          >
            Clear Chat
          </Button>
        </div>
      </div>

      <div style={styles.chatLayout}>
        {/* Messages Area */}
        <Card style={styles.chatCard}>
          <CardContent>
            <div style={styles.messagesContainer}>
              {messages.length === 0 ? (
                <div style={styles.welcomeScreen}>
                  <div style={styles.welcomeIcon}>
                    <Bot size={64} color="#6366f1" />
                  </div>
                  <h2 style={styles.welcomeTitle}>Hello! I'm your AI Trading Assistant</h2>
                  <p style={styles.welcomeSubtitle}>
                    Ask me anything about stocks, market data, portfolio analysis, or trading strategies
                  </p>

                  {/* Features Grid */}
                  <div style={styles.featuresGrid}>
                    {features.map((feature, idx) => (
                      <div key={idx} style={styles.featureItem}>
                        <div style={styles.featureIcon}>{feature.icon}</div>
                        <span style={styles.featureText}>{feature.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Example Queries */}
                  <div style={styles.examplesSection}>
                    <p style={styles.examplesTitle}>Try asking me:</p>
                    <div style={styles.examplesGrid}>
                      {exampleQueries.map((query, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleExampleClick(query.text)}
                          style={styles.exampleButton}
                          disabled={isLoading}
                        >
                          {query.icon}
                          <span>{query.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={styles.messagesList}>
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      style={{
                        ...styles.messageRow,
                        justifyContent: message.user ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {message.bot && (
                        <div style={styles.avatarBot}>
                          <Bot size={20} />
                        </div>
                      )}
                      <div
                        style={{
                          ...styles.messageBubble,
                          ...(message.user ? styles.userBubble : styles.botBubble)
                        }}
                      >
                        {renderMessageContent(message.user || message.bot)}
                      </div>
                      {message.user && (
                        <div style={styles.avatarUser}>
                          <UserIcon size={20} />
                        </div>
                      )}
                    </div>
                  ))}

                  {isLoading && (
                    <div style={{ ...styles.messageRow, justifyContent: 'flex-start' }}>
                      <div style={styles.avatarBot}>
                        <Bot size={20} />
                      </div>
                      <div style={{ ...styles.messageBubble, ...styles.botBubble }}>
                        <div style={styles.typingIndicator}>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Input Area */}
        <Card style={styles.inputCard}>
          <CardContent>
            <div style={styles.inputContainer}>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about stocks, markets, or your portfolio..."
                style={styles.input}
                rows={2}
                disabled={isLoading}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                variant="primary"
                icon={<Send size={18} />}
                style={styles.sendButton}
              >
                Send
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: theme.spacing[4],
    fontFamily: theme.typography.fontFamily.sans.join(', '),
    backgroundColor: 'var(--bg-secondary)',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    borderRadius: theme.borderRadius['2xl'],
    padding: theme.spacing[8],
    marginBottom: theme.spacing[6],
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing[4],
  },
  heroContent: {},
  heroTitle: {
    fontSize: theme.typography.fontSize['4xl'],
    fontWeight: theme.typography.fontWeight.bold,
    margin: 0,
    letterSpacing: '-0.025em',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[3],
  },
  heroSubtitle: {
    fontSize: theme.typography.fontSize.lg,
    color: 'white',
    opacity: 0.95,
    margin: '8px 0 0 0',
    fontWeight: theme.typography.fontWeight.medium,
    maxWidth: '600px',
  },
  heroActions: {
    display: 'flex',
    gap: theme.spacing[3],
  },
  chatLayout: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[4],
    minHeight: 0,
  },
  chatCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  messagesContainer: {
    height: '600px',
    overflowY: 'auto',
    padding: theme.spacing[4],
  },
  welcomeScreen: {
    textAlign: 'center',
    padding: theme.spacing[8],
  },
  welcomeIcon: {
    marginBottom: theme.spacing[4],
  },
  welcomeTitle: {
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
    marginBottom: theme.spacing[3],
  },
  welcomeSubtitle: {
    fontSize: theme.typography.fontSize.lg,
    color: 'var(--text-secondary)',
    maxWidth: '600px',
    margin: '0 auto',
    marginBottom: theme.spacing[8],
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: theme.spacing[4],
    marginBottom: theme.spacing[8],
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[3],
    padding: theme.spacing[4],
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: theme.borderRadius.lg,
    border: '1px solid var(--border-primary)',
  },
  featureIcon: {
    color: '#6366f1',
  },
  featureText: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
    textAlign: 'left',
  },
  examplesSection: {
    marginTop: theme.spacing[8],
  },
  examplesTitle: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: 'var(--text-primary)',
    marginBottom: theme.spacing[4],
  },
  examplesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: theme.spacing[3],
  },
  exampleButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
    padding: theme.spacing[3],
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-primary)',
    borderRadius: theme.borderRadius.lg,
    color: 'var(--text-primary)',
    fontSize: theme.typography.fontSize.sm,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left',
  },
  messagesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[4],
  },
  messageRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing[2],
  },
  avatarBot: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#6366f1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    flexShrink: 0,
  },
  avatarUser: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    flexShrink: 0,
  },
  messageBubble: {
    padding: theme.spacing[3],
    borderRadius: theme.borderRadius.lg,
    maxWidth: '70%',
    fontSize: theme.typography.fontSize.base,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  botBubble: {
    backgroundColor: 'var(--bg-tertiary)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-primary)',
  },
  userBubble: {
    backgroundColor: '#6366f1',
    color: 'white',
  },
  typingIndicator: {
    display: 'flex',
    gap: '6px',
    padding: '4px 0',
  },
  inputCard: {
    flexShrink: 0,
  },
  inputContainer: {
    display: 'flex',
    gap: theme.spacing[3],
    alignItems: 'flex-end',
  },
  input: {
    flex: 1,
    padding: theme.spacing[3],
    borderRadius: theme.borderRadius.lg,
    border: '1px solid var(--border-primary)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: theme.typography.fontSize.base,
    fontFamily: theme.typography.fontFamily.sans.join(', '),
    resize: 'none',
    outline: 'none',
  },
  sendButton: {
    flexShrink: 0,
  },
  stockCard: {
    backgroundColor: 'var(--bg-primary)',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing[3],
    border: '1px solid var(--border-primary)',
    minWidth: '280px',
  },
  stockHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing[2],
  },
  stockSymbol: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
  },
  stockPrice: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
  },
  stockDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[2],
  },
  stockChange: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[1],
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
  },
  stockGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing[2],
    marginTop: theme.spacing[2],
    paddingTop: theme.spacing[2],
    borderTop: '1px solid var(--border-primary)',
  },
  stockItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  stockLabel: {
    fontSize: theme.typography.fontSize.xs,
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
  },
  stockValue: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-primary)',
    fontWeight: theme.typography.fontWeight.medium,
  },
  jsonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[2],
  },
  jsonBlock: {
    backgroundColor: 'rgba(0,0,0,0.05)',
    padding: theme.spacing[2],
    borderRadius: theme.borderRadius.md,
    fontSize: theme.typography.fontSize.xs,
    overflowX: 'auto',
    margin: 0,
  },
};

// Add typing indicator animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes typing-dot {
    0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
    30% { opacity: 1; transform: translateY(-8px); }
  }
  
  [style*="typingIndicator"] span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: currentColor;
    display: inline-block;
    animation: typing-dot 1.4s infinite;
  }
  
  [style*="typingIndicator"] span:nth-child(2) {
    animation-delay: 0.2s;
  }
  
  [style*="typingIndicator"] span:nth-child(3) {
    animation-delay: 0.4s;
  }
  
  [style*="exampleButton"]:hover:not(:disabled) {
    background-color: var(--bg-tertiary);
    border-color: #6366f1;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  }
  
  [style*="exampleButton"]:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

document.head.appendChild(styleSheet);

export default ChatWindow;
